import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '../ui/icons';

const useCases = [
  {
    header: "Internal hiring isn't delivering, or the agency you hired isn't either?",
    body:
      "Your team has posted jobs and searched internally, or engaged with one agency, but qualified candidates still aren't appearing.",
    icon: '/assets/icons/icon_feat_metrics.webp',
    iconAlt: 'Performance metrics',
  },
  {
    header: 'You need to hire fast!',
    body: 'Project deadlines or growth plans require immediate hiring.',
    icon: '/assets/icons/icon_feat_faster_hiring.webp',
    iconAlt: 'Faster hiring',
  },
  {
    header: 'You need specialized talent!',
    body:
      "Access a wide network of licensed agencies through BoostHiring's organized process — in technology, engineering, finance, operations, and more.",
    icon: '/assets/icons/icon_feat_verified.webp',
    iconAlt: 'Verified talent',
  },
];

// eslint-disable-next-line import/prefer-default-export
export function UseCaseCarousel() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % useCases.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [resetKey]);

  const goTo = (idx: number) => {
    setActiveIndex(idx);
    setResetKey(k => k + 1);
  };
  const card = useCases[activeIndex];

  return (
    <div className="mt-12">
      <h3 className="text-2xl font-extrabold text-primary mb-2 text-center">
        Check these 3 most popular reasons why Employers use BoostHiring
      </h3>
      <p className="text-sm text-slate-600 text-center mb-6">
        Designed for companies that want faster hiring without long-term agency commitments.
      </p>
      <div className="relative max-w-3xl mx-auto">
        <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm px-14 py-8 h-[290px] overflow-hidden">
          <button
            type="button"
            onClick={() => goTo((activeIndex - 1 + useCases.length) % useCases.length)}
            aria-label="Previous use case"
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-primary hover:bg-slate-100 transition-all"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => goTo((activeIndex + 1) % useCases.length)}
            aria-label="Next use case"
            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-primary hover:bg-slate-100 transition-all"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
          <div
            key={activeIndex}
            className="flex flex-col items-center text-center animate-panel-fade-up"
          >
            <img
              src={card.icon}
              alt={card.iconAlt}
              width={256}
              height={256}
              loading="lazy"
              decoding="async"
              className="w-12 h-12 object-contain mb-4"
            />
            <h4 className="text-lg font-bold text-primary mb-4 leading-snug">{card.header}</h4>
            <p className="text-sm text-slate-900 leading-relaxed">{card.body}</p>
          </div>
        </div>
        <div className="flex justify-center gap-2 mt-4">
          {useCases.map((uc, idx) => (
            <button
              type="button"
              key={uc.header}
              onClick={() => goTo(idx)}
              aria-label={`Go to use case ${idx + 1}`}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                idx === activeIndex ? 'bg-primary scale-125' : 'bg-slate-300 hover:bg-slate-400'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
